import React, { useState, useEffect } from "react";
import { Plus, ArrowLeft, TagsFill, StarFill, List as GripIcon, Check2 } from "react-bootstrap-icons";

import AddItemButton from 'components/Buttons/AddItemButton';
import SaveButton from 'components/Buttons/SaveButton';
import SwipeableItem from 'components/SwipeableItem';
import SortableItem from 'components/SortableItem';
import Options from "components/Options";

import { SwipeProvider } from 'hoc/SwipeContext';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

import "./styles.css";

function List({
    items = [],
    onClickHandler,
    onDeleteHandler,
    onEditHandler,
    onReorderHandler,
    onBackClick,
    setCurrentState
}) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [sortedItems, setSortedItems] = useState(items);

    // Keep local order in sync if the items prop changes from outside
    // (e.g. after fetch/create/delete). Falls back to original array
    // position when order is null (e.g. before first reorder/save).
    useEffect(() => {
        const sorted = [...items].sort((a, b) => {
            const orderA = a.order ?? items.indexOf(a);
            const orderB = b.order ?? items.indexOf(b);
            return orderA - orderB;
        });
        setSortedItems(sorted);
    }, [items]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    const getItemId = (item, index) => item.uid || index;

    const handleDragEnd = (event) => {
        if (!isReorderMode) return;

        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = sortedItems.findIndex(
            (item, index) => getItemId(item, index) === active.id
        );
        const newIndex = sortedItems.findIndex(
            (item, index) => getItemId(item, index) === over.id
        );

        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(sortedItems, oldIndex, newIndex).map(
            (item, index) => ({ ...item, order: index })
        );
        setSortedItems(reordered);
    };

    const settings = {
        'section': 'groups',
        'items': {
            'reorder': {
                'label': 'Reorder',
                'type': 'button',
            },
        }
    };

    function onSettingChange(section, settingId) {
        switch (settingId) {
            case 'reorder':
                setIsReorderMode(!isReorderMode);
                break;

            default:
                break;
        }
    }

    const handleSaveOrder = () => {
        if (onReorderHandler) {
            onReorderHandler(sortedItems);
        }
        setIsReorderMode(false);
    };

    return (
        <div className="card card-paddings container-height">

            <div className="card-header list-header no-background no-border">
                <div className="p-2">

                    <div className="d-flex justify-content-between align-items-center">
                        <h3>Groups</h3>
                        <Options
                            settings={settings}
                            onChangeHandler={onSettingChange}
                            disabled={isReorderMode}
                        />
                    </div>

                    <span>Create groups to organize your favorites and tags</span>
                </div>
            </div>

            <div className="card-body overflow-auto p-0">

                {sortedItems.length > 0 ? (
                    <div className="flex-grow-1 overflow-auto">
                        <SwipeProvider>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                            >
                                <SortableContext
                                    items={sortedItems.map((item, index) => getItemId(item, index))}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <ul className="list-group list-group-flush w-100 mb-1">
                                        {sortedItems.map((item, index) => (
                                            <SortableItem
                                                key={getItemId(item, index)}
                                                id={getItemId(item, index)}
                                            >
                                                {(dragHandleProps) => (
                                                    <SwipeableItem
                                                        id={getItemId(item, index)}
                                                        onClick={isReorderMode ? null : () => onClickHandler(item)}
                                                        onEdit={!isReorderMode && onEditHandler ? () => onEditHandler(item) : null}
                                                        onDelete={!isReorderMode && onDeleteHandler ? () => onDeleteHandler(item.uid) : null}
                                                        swipeDisabled={isReorderMode}
                                                        className="position-relative overflow-hidden items-swipeable-wrapper"
                                                    >
                                                        <li
                                                            style={{
                                                                cursor: isReorderMode ? "default" : "pointer",
                                                            }}
                                                            className={`list-group-item d-flex align-items-center w-100 p-1  ${!isMobile && !isReorderMode ? 'list-group-item-action' : ''}`}
                                                        >
                                                            {isReorderMode && (
                                                                <span
                                                                    {...dragHandleProps}
                                                                    className="d-flex align-items-center pe-2 text-muted"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <GripIcon size={18} />
                                                                </span>
                                                            )}

                                                            <span className="flex-grow-1">{item.title}</span>

                                                            <div className="d-flex align-items-center">

                                                                <div className="d-flex flex-wrap gap-2 justify-content-end">

                                                                    <div className="d-flex gap-1 align-items-center">
                                                                        <TagsFill size={16} />
                                                                        {item.tags.length}
                                                                    </div>

                                                                    <div className="d-flex gap-1 align-items-center">
                                                                        <StarFill size={16} />
                                                                        {item.favorites.length}
                                                                    </div>

                                                                </div>
                                                            </div>

                                                        </li>
                                                    </SwipeableItem>
                                                )}
                                            </SortableItem>
                                        ))}
                                    </ul>
                                </SortableContext>
                            </DndContext>
                        </SwipeProvider>
                    </div>

                ) : !isMobile ? (
                    <div
                        className="d-flex flex-column justify-content-center align-items-center w-100 h-100"
                        onClick={() => setCurrentState('add')}
                        style={{ cursor: "pointer" }}
                    >
                        <Plus size={80} className="text-primary" />
                        <p className="text-muted mt-3">Click to add your first group</p>
                    </div>
                ) : (
                    <></>
                )}

            </div>

            {isMobile && (
                <div className="card-footer card-footer-paddings border-top no-background">

                    {isReorderMode ? (

                        <button
                            type="button"
                            className="btn btn-success w-100"
                            onClick={() => handleSaveOrder()}

                        >
                            <Check2 size={20} />
                            <span className="fw-semibold">Save</span>
                        </button>

                    ) : (
                        <div className="d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={() => setCurrentState('add')}
                            >
                                Add
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary ms-auto"
                                onClick={(e) => {
                                    e.preventDefault();

                                    onBackClick();
                                }}
                            >
                                <ArrowLeft className="me-2" />
                                Back
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!isMobile && sortedItems.length > 0 && (
                <div
                    className="card-footer card-footer-paddings no-border no-background mt-auto"
                >
                    {isReorderMode ? (
                        <SaveButton onClick={handleSaveOrder} />
                    ) : (
                        <AddItemButton onClick={() => setCurrentState('add')} />
                    )}
                </div>
            )}

        </div>
    );
};

export default List;